<template>
  <!-- 综合业务分析：客户/商机/合同整体走势与结构 -->
  <report-layout active="overall">
    <list-page>
      <search-condition @search="loadReport" :show-search="true" page-name="overallReport">
        <template #default>
          <yq-date-range v-model:start-time="query.startTime" v-model:end-time="query.endTime" class="double-width">
            <template #prefix>统计周期</template>
          </yq-date-range>
          <el-select v-model="query.dimension" placeholder="数据维度">
            <template #prefix>数据维度</template>
            <el-option label="按月" value="month"/>
            <el-option label="按季" value="quarter"/>
            <el-option label="按年" value="year"/>
          </el-select>
        </template>
        <template #option>
          <page-button @click="resetAll">重置</page-button>
          <page-button type="read" el-type="primary" @click="loadReport">查询</page-button>
        </template>
      </search-condition>

      <list-table :show-shadow="false">
        <div v-loading="loading" class="report-body">
          <div class="stat-grid">
            <div class="stat-card">
              <div class="label">客户总数</div>
              <div class="value">{{ customer.customerCount || 0 }}<span class="unit">家</span></div>
            </div>
            <div class="stat-card">
              <div class="label">商机总数</div>
              <div class="value">{{ opportunity.opportunityCount || 0 }}<span class="unit">个</span></div>
            </div>
            <div class="stat-card">
              <div class="label">合同金额</div>
              <div class="value money">{{ money(contract.totalAmount) }}<span class="unit">元</span></div>
            </div>
          </div>

          <div class="chart-card">
            <div class="section-title">经营走势总览（合同金额·万元）</div>
            <div ref="trendChart" class="chart-div"></div>
          </div>

          <div class="grid-3">
            <div class="chart-card">
              <div class="section-title">客户结构</div>
              <div class="donut-wrap">
                <div ref="customerChart" class="donut-div"></div>
                <div class="donut-legend">
                  <div v-for="row in customerTypeRows" :key="row.name" class="lg-row">
                    <span class="lg-dot" :style="{background: row.color}"></span>
                    <span class="lg-name">{{ row.name }}</span>
                    <span class="lg-val">{{ row.value }}</span>
                  </div>
                  <div v-if="!customerTypeRows.length" class="lg-empty">暂无数据</div>
                </div>
              </div>
            </div>
            <div class="chart-card">
              <div class="section-title">商机漏斗</div>
              <div ref="funnelChart" class="chart-div"></div>
            </div>
            <div class="chart-card">
              <div class="section-title">业绩排行</div>
              <div class="rank-list">
                <div v-for="(row, index) in rankRows" :key="row.employeeId" class="rank-item">
                  <span class="rank-no" :class="index < 3 ? 't' + (index + 1) : ''">{{ index + 1 }}</span>
                  <span class="rank-name">{{ row.employeeName }}</span>
                  <span class="rank-val">{{ money(row.achieved) }}</span>
                </div>
                <div v-if="!rankRows.length" class="muted" style="padding: 12px 4px">暂无数据</div>
              </div>
            </div>
          </div>

          <div class="chart-card">
            <div class="section-title">综合业务统计明细</div>
            <el-table :data="tableRows" border size="small" show-summary :summary-method="summaryMethod" empty-text="暂无数据">
              <el-table-column :label="dimensionLabel" prop="label" min-width="130"/>
              <el-table-column label="新增客户" prop="newCustomer" align="right" min-width="110">
                <template v-slot="{row}"><span class="num">{{ row.newCustomer }}</span></template>
              </el-table-column>
              <el-table-column label="新增商机" prop="newOpportunity" align="right" min-width="110">
                <template v-slot="{row}"><span class="num">{{ row.newOpportunity }}</span></template>
              </el-table-column>
              <el-table-column label="合同数量" prop="contractCount" align="right" min-width="110">
                <template v-slot="{row}"><span class="num">{{ row.contractCount }}</span></template>
              </el-table-column>
              <el-table-column label="合同金额(万)" prop="contractAmount" align="right" min-width="140">
                <template v-slot="{row}"><span class="num bold">{{ row.contractAmount }}</span></template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </list-table>
    </list-page>
  </report-layout>
</template>

<script>
import * as echarts from "echarts";
import {markRaw} from "vue";
import ReportLayout from "@/views/h5/crm/report/ReportLayout.vue";
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import YqDateRange from "@/components/h5/date/YqDateRange.vue";
import customerStatApi from "@/api/dz-crm/partner/customerStatApi";
import opportunityOverviewApi from "@/api/dz-crm/partner/opportunityOverviewApi";
import contractApi from "@/api/dz-crm/partner/contractApi";
import saleTargetApi from "@/api/dz-crm/sale/saleTargetApi";
import commonApi from "@/api/commonApi";
import {DICT_CUSTOMER_TYPE, DICT_OPPORTUNITY_STAGE, fillStageRows} from "@/views/h5/crm/crmDictionary";
import {toChartRows} from "@/views/h5/crm/customerStat";
import {
  barOption,
  cleanParams,
  donutOption,
  funnelOption,
  money,
} from "@/views/h5/crm/report/reportUtil";

const DIMENSION_LABEL = {month: "月份", quarter: "季度", year: "年度"};

function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function monthKey(key, dimension) {
  if (dimension === "quarter") {
    const [year, month] = key.split("-");
    return `${year}-Q${Math.ceil(Number(month) / 3)}`;
  }
  if (dimension === "year") {
    return key.slice(0, 4);
  }
  return key;
}

export default {
  name: "OverallReport",
  components: {ReportLayout, ListPage, ListTable, SearchCondition, PageButton, YqDateRange},
  data() {
    const year = new Date().getFullYear();
    return {
      loading: false,
      query: {startTime: `${year}-01-01`, endTime: today(), dimension: "month"},
      customer: {},
      opportunity: {},
      contract: {},
      performance: {},
      customerTypeList: [],
      stageList: [],
      dictReady: false,
      dataReady: false,
      trendChart: null,
      customerChart: null,
      funnelChart: null,
    };
  },
  computed: {
    dimensionLabel() {
      return DIMENSION_LABEL[this.query.dimension] || "月份";
    },
    customerTypeRows() {
      return toChartRows(this.customer.typeStats, this.customerTypeList);
    },
    funnelRows() {
      return fillStageRows(this.stageList, this.opportunity.stageStats, {excludeClose: true})
          .map(row => ({name: row.stageTitle, value: row.opportunityCount || 0}));
    },
    rankRows() {
      return (this.performance.rows || [])
          .slice()
          .sort((a, b) => Number(b.achieved || 0) - Number(a.achieved || 0))
          .slice(0, 8);
    },
    /** 客户/商机/合同三份月度数据按月份合并，再按所选维度聚合 */
    tableRows() {
      const customerMap = this.toMonthMap(this.customer.trendMonthList, this.customer.trendCountList);
      const opportunityMap = this.toMonthMap(this.opportunity.trendMonthList, this.opportunity.trendCountList);
      const contractCountMap = this.toMonthMap(this.contract.monthList, this.contract.contractCountList);
      const contractAmountMap = this.toMonthMap(this.contract.monthList,
          (this.contract.amountList || []).map(value => Number(value || 0)));

      const months = Array.from(new Set([
        ...Object.keys(customerMap),
        ...Object.keys(opportunityMap),
        ...Object.keys(contractCountMap),
      ])).sort();

      const result = new Map();
      months.forEach(month => {
        const key = monthKey(month, this.query.dimension);
        if (!result.has(key)) {
          result.set(key, {label: key, newCustomer: 0, newOpportunity: 0, contractCount: 0, amount: 0});
        }
        const row = result.get(key);
        row.newCustomer += customerMap[month] || 0;
        row.newOpportunity += opportunityMap[month] || 0;
        row.contractCount += contractCountMap[month] || 0;
        row.amount += contractAmountMap[month] || 0;
      });
      return Array.from(result.values()).map(row => ({
        ...row,
        contractAmount: Math.round(row.amount / 100 / 100) / 100,
      }));
    },
  },
  watch: {
    "query.dimension"() {
      // 维度只影响聚合方式，不用重新请求接口
      this.$nextTick(() => this.renderIfReady());
    },
  },
  beforeRouteLeave(to, from, next) {
    next();
  },
  mounted() {
    Promise.all([
      commonApi.queryUniqueDictionaryList(DICT_CUSTOMER_TYPE),
      commonApi.queryUniqueDictionaryList(DICT_OPPORTUNITY_STAGE),
    ]).then(([customerType, stage]) => {
      this.customerTypeList = customerType || [];
      this.stageList = stage || [];
      this.dictReady = true;
      this.renderIfReady();
    });
    this.loadReport();
  },
  unmounted() {
    [this.trendChart, this.customerChart, this.funnelChart].forEach(chart => chart && chart.dispose());
  },
  methods: {
    money,
    toMonthMap(months, values) {
      const map = {};
      (months || []).forEach((month, index) => {
        map[month] = Number((values || [])[index] || 0);
      });
      return map;
    },
    async loadReport() {
      this.loading = true;
      try {
        const params = cleanParams({
          startTime: this.query.startTime ? this.query.startTime.slice(0, 10) : "",
          endTime: this.query.endTime ? this.query.endTime.slice(0, 10) : "",
        });
        const year = params.startTime ? Number(params.startTime.slice(0, 4)) : new Date().getFullYear();
        const [customer, opportunity, contract, performance] = await Promise.all([
          customerStatApi.overview(params),
          opportunityOverviewApi.overview(params),
          contractApi.statDistribution(params),
          saleTargetApi.performance({year, businessType: 1}),
        ]);
        this.customer = customer || {};
        this.opportunity = opportunity || {};
        this.contract = contract || {};
        this.performance = performance || {};
        this.dataReady = true;
        this.$nextTick(() => this.renderIfReady());
      } finally {
        this.loading = false;
      }
    },
    resetAll() {
      const year = new Date().getFullYear();
      this.query = {startTime: `${year}-01-01`, endTime: today(), dimension: "month"};
      this.loadReport();
    },
    renderIfReady() {
      if (this.dictReady && this.dataReady) {
        this.renderCharts();
      }
    },
    renderCharts() {
      const rows = this.tableRows;
      this.trendChart = this.setOption(this.trendChart, this.$refs.trendChart,
          barOption(rows.map(row => row.label), rows.map(row => row.contractAmount), "#1677ff"));
      this.customerChart = this.setOption(this.customerChart, this.$refs.customerChart,
          donutOption(this.customerTypeRows, this.customer.customerCount || 0, "客户数量"));
      this.funnelChart = this.setOption(this.funnelChart, this.$refs.funnelChart,
          funnelOption(this.funnelRows));
    },
    setOption(chart, dom, option) {
      if (!dom) {
        return chart;
      }
      if (!chart) {
        chart = markRaw(echarts.init(dom));
      }
      chart.setOption(option, true);
      return chart;
    },
    summaryMethod({columns}) {
      return columns.map((column, index) => {
        if (index === 0) {
          return "合计";
        }
        if (column.property === "newCustomer" || column.property === "newOpportunity" || column.property === "contractCount") {
          const total = this.tableRows.reduce((sum, row) => sum + Number(row[column.property] || 0), 0);
          return String(total);
        }
        if (column.property === "contractAmount") {
          const total = this.tableRows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
          return String(Math.round(total / 100) / 100);
        }
        return "";
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/views/h5/crm/report/report.scss";

// 三列布局里环形图收小一点，给图例留空间
.grid-3 {
  .donut-div {
    width: 150px;
    height: 150px;
  }

  .donut-wrap {
    gap: 8px;
  }
}
</style>
